import { useEffect, useState } from "react";
import { http, CATEGORIES, POLL_TOPICS, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import StatsTab from "./admin/StatsTab";
import SubscribersTab from "./admin/SubscribersTab";
import NewsletterTab from "./admin/NewsletterTab";
import ImageOfDayTab from "./admin/ImageOfDayTab";
import TrafficTodayTab from "./admin/TrafficTodayTab";

const EMPTY_ARTICLE = {
  title: "", summary: "", body: "", source_id: "", url: "",
  category: "presidencia", image_url: "", author: "", featured: false, order: 0,
};

const EMPTY_RESULT = { candidate: "", party: "", percentage: 0 };
const EMPTY_POLL = {
  pollster: "", date: new Date().toISOString().slice(0, 10),
  sample_size: 1000, topic: "approval", notes: "",
  results: [{ ...EMPTY_RESULT }],
};

const EMPTY_PRESS = {
  secretaria: "", secretaria_full: "", title: "", url: "",
  date: new Date().toISOString().slice(0, 10),
};

function ArticlesTab() {
  const [articles, setArticles] = useState([]);
  const [sources, setSources] = useState([]);
  const [form, setForm] = useState(EMPTY_ARTICLE);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [msg, setMsg] = useState("");

  const load = async () => {
    const [a, s] = await Promise.all([http.get("/articles", { params: { limit: 500 } }), http.get("/sources")]);
    setArticles(a.data);
    setSources(s.data);
    if (s.data[0] && !form.source_id) setForm((f) => ({ ...f, source_id: s.data[0].id }));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const reset = () => { setForm({ ...EMPTY_ARTICLE, source_id: sources[0]?.id || "" }); setEditingId(null); setError(""); };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const payload = { ...form, order: Number(form.order) || 0 };
    try {
      if (editingId) await http.put(`/articles/${editingId}`, payload);
      else await http.post("/articles", payload);
      reset();
      await load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const edit = (a) => {
    setEditingId(a.id);
    setForm({
      title: a.title, summary: a.summary || "", body: a.body || "",
      source_id: a.source_id, url: a.url, category: a.category,
      image_url: a.image_url || "", author: a.author || "",
      featured: a.featured, order: a.order || 0,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar artículo?")) return;
    await http.delete(`/articles/${id}`);
    await load();
  };

  const refreshNews = async () => {
    setRefreshing(true);
    setMsg("");
    try {
      const { data } = await http.post("/admin/refresh-news");
      setMsg(`+${data.inserted} nuevos · ${data.total} totales en base`);
      await load();
    } catch (e) {
      setMsg("Error al actualizar: " + (e.response?.data?.detail || e.message));
    } finally {
      setRefreshing(false);
    }
  };

  const filtered = articles.filter((a) =>
    !filter || a.title.toLowerCase().includes(filter.toLowerCase()) || a.category === filter
  );

  return (
    <div data-testid="admin-articles-tab">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <button
          onClick={refreshNews}
          disabled={refreshing}
          className="border border-ink px-4 py-2 font-mono text-[12px] uppercase tracking-widest hover:bg-ink hover:text-paper transition-colors disabled:opacity-40"
          data-testid="btn-refresh-news"
        >
          {refreshing ? "Buscando…" : "↻ Actualizar desde RSS"}
        </button>
        <input
          placeholder="Filtrar por título o categoría exacta"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="border border-ink/30 px-3 py-2 text-sm rounded-none bg-transparent w-72"
          data-testid="art-filter"
        />
        {msg && <span className="font-mono text-[11px] text-sub">{msg}</span>}
      </div>

      <div className="grid lg:grid-cols-12 gap-8">
        <form onSubmit={submit} className="lg:col-span-5 border border-ink bg-white p-5 space-y-3" data-testid="article-form">
          <h3 className="font-serif-display text-2xl">{editingId ? "Editar artículo" : "Nueva nota"}</h3>
          <input className="w-full border border-ink/30 px-3 py-2 text-sm rounded-none bg-transparent" placeholder="Título" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="art-title" />
          <textarea className="w-full border border-ink/30 px-3 py-2 text-sm rounded-none bg-transparent" placeholder="Resumen" rows={2} value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} data-testid="art-summary" />
          <div className="grid grid-cols-2 gap-3">
            <select className="border border-ink/30 px-3 py-2 text-sm rounded-none bg-transparent" value={form.source_id} onChange={(e) => setForm({ ...form, source_id: e.target.value })} required data-testid="art-source">
              {sources.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
            <select className="border border-ink/30 px-3 py-2 text-sm rounded-none bg-transparent" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="art-category">
              {CATEGORIES.map((c) => <option key={c.slug} value={c.slug}>{c.label}</option>)}
            </select>
          </div>
          <input className="w-full border border-ink/30 px-3 py-2 text-sm rounded-none bg-transparent" placeholder="URL fuente original" required value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} data-testid="art-url" />
          <input className="w-full border border-ink/30 px-3 py-2 text-sm rounded-none bg-transparent" placeholder="URL de imagen (opcional)" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} data-testid="art-image" />
          <input className="w-full border border-ink/30 px-3 py-2 text-sm rounded-none bg-transparent" placeholder="Autor (opcional)" value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} data-testid="art-author" />
          <div className="grid grid-cols-2 gap-3">
            <label className="flex items-center gap-2 font-sans text-sm">
              <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} data-testid="art-featured" />
              Destacada
            </label>
            <input type="number" className="border border-ink/30 px-3 py-2 text-sm rounded-none bg-transparent" placeholder="Orden" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} data-testid="art-order" />
          </div>
          {error && <p className="text-rojo text-sm" data-testid="art-error">{error}</p>}
          <div className="flex gap-2">
            <button type="submit" className="flex-1 border border-ink py-2 font-mono text-[12px] uppercase tracking-widest hover:bg-ink hover:text-paper transition-colors" data-testid="art-submit">
              {editingId ? "Actualizar" : "Publicar"}
            </button>
            {editingId && (
              <button type="button" onClick={reset} className="border border-ink/40 px-4 font-mono text-[12px] uppercase tracking-widest hover:bg-ink/5">
                Cancelar
              </button>
            )}
          </div>
        </form>

        <div className="lg:col-span-7" data-testid="article-table">
          <h3 className="font-serif-display text-2xl mb-3">Notas ({filtered.length} / {articles.length})</h3>
          <div className="border border-ink bg-white max-h-[640px] overflow-y-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-ink text-paper sticky top-0">
                <tr>
                  <th className="px-3 py-2 font-mono text-[11px] uppercase tracking-widest">Título</th>
                  <th className="px-3 py-2 font-mono text-[11px] uppercase tracking-widest">Cat.</th>
                  <th className="px-3 py-2 font-mono text-[11px] uppercase tracking-widest">Fuente</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((a) => (
                  <tr key={a.id} className="border-t border-rule" data-testid={`art-row-${a.id}`}>
                    <td className="px-3 py-2 max-w-md">
                      {a.featured && <span className="text-rojo mr-1">★</span>}
                      {a.title}
                    </td>
                    <td className="px-3 py-2 text-sub whitespace-nowrap">{a.category}</td>
                    <td className="px-3 py-2 text-sub">{a.source_code}</td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <button onClick={() => edit(a)} className="font-mono text-[11px] uppercase tracking-widest hover:text-rojo mr-3" data-testid={`art-edit-${a.id}`}>Editar</button>
                      <button onClick={() => remove(a.id)} className="font-mono text-[11px] uppercase tracking-widest text-rojo" data-testid={`art-delete-${a.id}`}>Borrar</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function PollsTab() {
  const [polls, setPolls] = useState([]);
  const [form, setForm] = useState(EMPTY_POLL);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState("");

  const load = async () => {
    const { data } = await http.get("/polls", { params: { limit: 200 } });
    setPolls(data);
  };
  useEffect(() => { load(); }, []);

  const reset = () => { setForm({ ...EMPTY_POLL, results: [{ ...EMPTY_RESULT }] }); setEditingId(null); setError(""); };

  const setRes = (i, k, v) => {
    const r = [...form.results];
    r[i] = { ...r[i], [k]: k === "percentage" ? Number(v) : v };
    setForm({ ...form, results: r });
  };
  const addRes = () => setForm({ ...form, results: [...form.results, { ...EMPTY_RESULT }] });
  const rmRes = (i) => setForm({ ...form, results: form.results.filter((_, x) => x !== i) });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const payload = {
      ...form,
      sample_size: form.sample_size ? Number(form.sample_size) : null,
      results: form.results.filter((r) => r.candidate.trim()),
    };
    try {
      if (editingId) await http.put(`/polls/${editingId}`, payload);
      else await http.post("/polls", payload);
      reset();
      await load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const edit = (p) => { setEditingId(p.id); setForm({ ...p }); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const remove = async (id) => {
    if (!window.confirm("¿Eliminar encuesta?")) return;
    await http.delete(`/polls/${id}`);
    await load();
  };

  return (
    <div className="grid lg:grid-cols-12 gap-8" data-testid="admin-polls-tab">
      <form onSubmit={submit} className="lg:col-span-5 border border-ink bg-white p-5 space-y-3" data-testid="poll-form">
        <h3 className="font-serif-display text-2xl">{editingId ? "Editar encuesta" : "Nueva encuesta"}</h3>
        <input className="w-full border border-ink/30 px-3 py-2 text-sm rounded-none bg-transparent" placeholder="Casa encuestadora" required value={form.pollster} onChange={(e) => setForm({ ...form, pollster: e.target.value })} data-testid="poll-pollster" />
        <div className="grid grid-cols-2 gap-3">
          <input type="date" className="border border-ink/30 px-3 py-2 text-sm rounded-none bg-transparent" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="poll-date" />
          <input type="number" className="border border-ink/30 px-3 py-2 text-sm rounded-none bg-transparent" placeholder="Muestra" value={form.sample_size || ""} onChange={(e) => setForm({ ...form, sample_size: e.target.value })} data-testid="poll-sample" />
        </div>
        <select className="w-full border border-ink/30 px-3 py-2 text-sm rounded-none bg-transparent" value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })} data-testid="poll-topic">
          {POLL_TOPICS.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
        </select>
        <textarea className="w-full border border-ink/30 px-3 py-2 text-sm rounded-none bg-transparent" placeholder="Notas (opcional)" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        <div>
          <p className="font-mono text-[11px] uppercase tracking-widest text-sub mb-2">Resultados</p>
          {form.results.map((r, i) => (
            <div key={i} className="grid grid-cols-12 gap-2 mb-2">
              <input className="col-span-5 border border-ink/30 px-2 py-1 text-sm rounded-none bg-transparent" placeholder="Opción / Candidato" value={r.candidate} onChange={(e) => setRes(i, "candidate", e.target.value)} data-testid={`poll-cand-${i}`} />
              <input className="col-span-3 border border-ink/30 px-2 py-1 text-sm rounded-none bg-transparent" placeholder="Partido" value={r.party} onChange={(e) => setRes(i, "party", e.target.value.toUpperCase())} data-testid={`poll-party-${i}`} />
              <input type="number" step="0.1" className="col-span-3 border border-ink/30 px-2 py-1 text-sm rounded-none bg-transparent" placeholder="%" value={r.percentage} onChange={(e) => setRes(i, "percentage", e.target.value)} data-testid={`poll-pct-${i}`} />
              <button type="button" className="col-span-1 text-rojo text-sm" onClick={() => rmRes(i)}>×</button>
            </div>
          ))}
          <button type="button" onClick={addRes} className="font-mono text-[11px] uppercase tracking-widest text-sub hover:text-ink" data-testid="poll-add-row">
            + agregar fila
          </button>
        </div>
        {error && <p className="text-rojo text-sm">{error}</p>}
        <div className="flex gap-2">
          <button type="submit" className="flex-1 border border-ink py-2 font-mono text-[12px] uppercase tracking-widest hover:bg-ink hover:text-paper transition-colors" data-testid="poll-submit">
            {editingId ? "Actualizar" : "Publicar"}
          </button>
          {editingId && (
            <button type="button" onClick={reset} className="border border-ink/40 px-4 font-mono text-[12px] uppercase tracking-widest hover:bg-ink/5">
              Cancelar
            </button>
          )}
        </div>
      </form>

      <div className="lg:col-span-7" data-testid="poll-table">
        <h3 className="font-serif-display text-2xl mb-3">Encuestas ({polls.length})</h3>
        <div className="border border-ink bg-white max-h-[640px] overflow-y-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-ink text-paper sticky top-0">
              <tr>
                <th className="px-3 py-2 font-mono text-[11px] uppercase tracking-widest">Fecha</th>
                <th className="px-3 py-2 font-mono text-[11px] uppercase tracking-widest">Casa</th>
                <th className="px-3 py-2 font-mono text-[11px] uppercase tracking-widest">Tema</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {polls.map((p) => (
                <tr key={p.id} className="border-t border-rule" data-testid={`poll-row-${p.id}`}>
                  <td className="px-3 py-2 font-mono">{p.date}</td>
                  <td className="px-3 py-2">{p.pollster}</td>
                  <td className="px-3 py-2 text-sub">{p.topic}</td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <button onClick={() => edit(p)} className="font-mono text-[11px] uppercase tracking-widest hover:text-rojo mr-3" data-testid={`poll-edit-${p.id}`}>Editar</button>
                    <button onClick={() => remove(p.id)} className="font-mono text-[11px] uppercase tracking-widest text-rojo" data-testid={`poll-delete-${p.id}`}>Borrar</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function PressTab() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY_PRESS);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState("");

  const load = async () => {
    const { data } = await http.get("/press-releases", { params: { limit: 200 } });
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const reset = () => { setForm(EMPTY_PRESS); setEditingId(null); setError(""); };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      if (editingId) await http.put(`/press-releases/${editingId}`, form);
      else await http.post("/press-releases", form);
      reset();
      await load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const edit = (p) => { setEditingId(p.id); setForm({ ...p }); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const remove = async (id) => {
    if (!window.confirm("¿Eliminar comunicado?")) return;
    await http.delete(`/press-releases/${id}`);
    await load();
  };

  return (
    <div className="grid lg:grid-cols-12 gap-8" data-testid="admin-press-tab">
      <form onSubmit={submit} className="lg:col-span-5 border border-ink bg-white p-5 space-y-3" data-testid="press-form">
        <h3 className="font-serif-display text-2xl">{editingId ? "Editar comunicado" : "Nuevo comunicado"}</h3>
        <p className="text-xs text-sub leading-snug">
          Curaduría manual. Los comunicados permanecen visibles hasta que los borres o uses
          <code className="bg-ink/5 px-1 mx-1">POST /admin/press-releases/purge-old</code>
          (limpia los de más de 30 días).
        </p>
        <div className="grid grid-cols-3 gap-3">
          <input className="col-span-1 border border-ink/30 px-3 py-2 text-sm rounded-none bg-transparent" placeholder="SHCP" required value={form.secretaria} onChange={(e) => setForm({ ...form, secretaria: e.target.value.toUpperCase() })} data-testid="press-code" />
          <input className="col-span-2 border border-ink/30 px-3 py-2 text-sm rounded-none bg-transparent" placeholder="Nombre completo (Hacienda…)" required value={form.secretaria_full} onChange={(e) => setForm({ ...form, secretaria_full: e.target.value })} data-testid="press-full" />
        </div>
        <input className="w-full border border-ink/30 px-3 py-2 text-sm rounded-none bg-transparent" placeholder="Título del comunicado" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="press-title" />
        <input className="w-full border border-ink/30 px-3 py-2 text-sm rounded-none bg-transparent" placeholder="https://www.gob.mx/<dependencia>/prensa/<slug-de-la-nota>" required value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} data-testid="press-url" />
        <p className="text-[11px] text-sub leading-snug -mt-2">
          Pega el link <strong>directo</strong> del comunicado (no el listado de prensa ni el home). Ej: <code className="bg-ink/5 px-1">gob.mx/sre/prensa/comunicado-no-15</code>.
        </p>
        <input type="date" className="w-full border border-ink/30 px-3 py-2 text-sm rounded-none bg-transparent" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="press-date" />
        {error && <p className="text-rojo text-sm">{error}</p>}
        <div className="flex gap-2">
          <button type="submit" className="flex-1 border border-ink py-2 font-mono text-[12px] uppercase tracking-widest hover:bg-ink hover:text-paper transition-colors" data-testid="press-submit">
            {editingId ? "Actualizar" : "Publicar"}
          </button>
          {editingId && (
            <button type="button" onClick={reset} className="border border-ink/40 px-4 font-mono text-[12px] uppercase tracking-widest hover:bg-ink/5">
              Cancelar
            </button>
          )}
        </div>
      </form>

      <div className="lg:col-span-7" data-testid="press-table">
        <h3 className="font-serif-display text-2xl mb-3">Comunicados ({items.length})</h3>
        <div className="border border-ink bg-white max-h-[640px] overflow-y-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-ink text-paper sticky top-0">
              <tr>
                <th className="px-3 py-2 font-mono text-[11px] uppercase tracking-widest">Fecha</th>
                <th className="px-3 py-2 font-mono text-[11px] uppercase tracking-widest">Sec.</th>
                <th className="px-3 py-2 font-mono text-[11px] uppercase tracking-widest">Título</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.id} className="border-t border-rule" data-testid={`press-row-${p.id}`}>
                  <td className="px-3 py-2 font-mono">{p.date}</td>
                  <td className="px-3 py-2">{p.secretaria}</td>
                  <td className="px-3 py-2">{p.title}</td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <button onClick={() => edit(p)} className="font-mono text-[11px] uppercase tracking-widest hover:text-rojo mr-3" data-testid={`press-edit-${p.id}`}>Editar</button>
                    <button onClick={() => remove(p.id)} className="font-mono text-[11px] uppercase tracking-widest text-rojo" data-testid={`press-delete-${p.id}`}>Borrar</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default function Admin() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("traffic");

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-10 animate-fade-in" data-testid="admin-page">
      <div className="flex items-end justify-between flex-wrap gap-4 border-b-2 border-ink pb-3">
        <div>
          <p className="section-eyebrow">PANEL EDITORIAL</p>
          <h1 className="font-serif-display text-4xl sm:text-5xl">Curaduría diaria</h1>
        </div>
        <div className="flex items-center gap-4">
          <span className="font-mono text-[12px] uppercase tracking-widest text-sub" data-testid="admin-email">
            {user?.email}
          </span>
          <button
            onClick={logout}
            className="border border-ink px-4 py-1 font-mono text-[12px] uppercase tracking-widest hover:bg-ink hover:text-paper transition-colors"
            data-testid="admin-logout"
          >
            Salir
          </button>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap border border-ink w-fit" data-testid="admin-tabs">
        {[
          { key: "traffic", label: "Tráfico hoy" },
          { key: "articles", label: "Artículos" },
          { key: "polls", label: "Encuestas" },
          { key: "press", label: "Comunicados" },
          { key: "iod", label: "Imagen del día" },
          { key: "newsletter", label: "Newsletter" },
          { key: "stats", label: "Estadísticas" },
          { key: "subscribers", label: "Suscriptores" },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-5 py-2 font-mono text-[12px] uppercase tracking-widest ${
              tab === t.key ? "bg-ink text-paper" : "hover:bg-ink/10"
            }`}
            data-testid={`tab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="mt-8">
        {tab === "traffic" && <TrafficTodayTab />}
        {tab === "articles" && <ArticlesTab />}
        {tab === "polls" && <PollsTab />}
        {tab === "press" && <PressTab />}
        {tab === "iod" && <ImageOfDayTab />}
        {tab === "newsletter" && <NewsletterTab />}
        {tab === "stats" && <StatsTab />}
        {tab === "subscribers" && <SubscribersTab />}
      </div>
    </div>
  );
}
